import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import axios from "../../config/axios";
import { useCart } from "./CartContext";
import { useToast } from "./ToastContext";
import { useAuth } from "./AuthContext";
import { useLocation } from "react-router-dom";

/* ------------------- STEP CONTEXT (with sub-steps) ------------------- */
const StepContext = createContext(null);

const STEP_FLOW = [
  "1",
  "1.1",
  "2",
  "3.1",
  "3.2",
  "3.3",
  "4",
  "5",
  "6",
  "7",
  "8",
];

export const StepProvider = ({ children }) => {
  const [activeStep, setActiveStep] = useState(STEP_FLOW[0]);

  const nextStep = useCallback(() => {
    setActiveStep((curr) => {
      const idx = STEP_FLOW.indexOf(curr);
      return idx < STEP_FLOW.length - 1 ? STEP_FLOW[idx + 1] : curr;
    });
  }, []);

  const prevStep = useCallback(() => {
    setActiveStep((curr) => {
      const idx = STEP_FLOW.indexOf(curr);
      return idx > 0 ? STEP_FLOW[idx - 1] : curr;
    });
  }, []);

  const goToStep = useCallback((step) => {
    if (STEP_FLOW.includes(step)) setActiveStep(step);
  }, []);

  const value = useMemo(
    () => ({
      activeStep,
      setActiveStep,
      nextStep,
      prevStep,
      goToStep,
      steps: STEP_FLOW,
    }),
    [activeStep, nextStep, prevStep, goToStep],
  );

  return <StepContext.Provider value={value}>{children}</StepContext.Provider>;
};

export const useStep = () => useContext(StepContext);

/* ------------------- PLAN CONTEXT ------------------- */
const PlanContext = createContext(null);

export const PlanProvider = ({ children }) => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  useEffect(() => {
    try {
      const saved = localStorage.getItem("selectedPlanNew");
      if (saved) {
        const parsed = JSON.parse(saved);
        setSelectedPlan(parsed);
      }
    } catch (err) {
      console.error("Error reading selectedPlan from localStorage:", err);
      // If invalid data found, clear it
      localStorage.removeItem("selectedPlanNew");
    }
  }, []);
  const value = useMemo(
    () => ({ selectedPlan, setSelectedPlan }),
    [selectedPlan],
  );

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
};
export const usePlan = () => useContext(PlanContext);

/* ------------------- MEAL TYPE CONTEXT ------------------- */
const MealTypeContext = createContext(null);

export const MealTypeProvider = ({ children }) => {
  const [selectedMealType, setSelectedMealType] = useState(null);

  // ✅ Load from localStorage on mount
  useEffect(() => {
    const savedMealType = localStorage.getItem("selectedMealTypeNew");
    if (savedMealType) {
      setSelectedMealType(savedMealType);
    }
  }, []);

  const value = useMemo(
    () => ({ selectedMealType, setSelectedMealType }),
    [selectedMealType],
  );

  return (
    <MealTypeContext.Provider value={value}>
      {children}
    </MealTypeContext.Provider>
  );
};

export const useMealType = () => useContext(MealTypeContext);

/* ------------------- MEALS SELECTION CONTEXT ------------------- */
const MealsSelectionContext = createContext(null);

export const MealsSelectionProvider = ({ children }) => {
  const [selectedMeals, setSelectedMeals] = useState([]);
  useEffect(() => {
    try {
      const saved = localStorage.getItem("selectedMealsNew");
      if (saved) {
        const parsed = JSON.parse(saved);
        setSelectedMeals(parsed);
      }
    } catch (err) {
      console.error("Error reading selectedPlan from localStorage:", err);
      // If invalid data found, clear it
      localStorage.removeItem("selectedMealsNew");
    }
  }, []);
  // Define order mappi
  const addMeal = useCallback(
    (meal) =>
      setSelectedMeals((prev) => {
        const updated = [...prev, meal];
        return updated;
      }),
    [],
  );

  const removeMeal = useCallback(
    (mealId) =>
      setSelectedMeals((prev) => {
        const updated = prev.filter((meal) => meal._id !== mealId);
        return updated;
      }),
    [],
  );

  const clearMeals = useCallback(() => setSelectedMeals([]), []);

  const value = useMemo(
    () => ({
      selectedMeals,
      setSelectedMeals: (meals) => setSelectedMeals(meals),
      addMeal,
      removeMeal,
      clearMeals,
    }),
    [selectedMeals, addMeal, removeMeal, clearMeals],
  );

  return (
    <MealsSelectionContext.Provider value={value}>
      {children}
    </MealsSelectionContext.Provider>
  );
};

export const useMealsSelection = () => useContext(MealsSelectionContext);

/* ------------------- CHILD DETAILS CONTEXT ------------------- */
const ChildDetailsContext = createContext(null);

export const ChildDetailsProvider = ({ children }) => {
  const [childDetails, setChildDetails] = useState({
    firstName: "",
    lastName: "",
    age: "",
    gender: "",
    school: "",
  });
  useEffect(() => {
    try {
      const saved = localStorage.getItem("childDetailsNew");
      if (saved) {
        const parsed = JSON.parse(saved);
        setChildDetails(parsed);
      }
    } catch (err) {
      console.error("Error reading selectedPlan from localStorage:", err);
    }
  }, []);
  const updateChildDetails = useCallback(
    (updates) => setChildDetails((prev) => ({ ...prev, ...updates })),
    [],
  );
  const clearChildDetails = useCallback(
    () =>
      setChildDetails({
        firstName: "",
        lastName: "",
        age: "",
        gender: "",
        school: "",
      }),
    [],
  );

  const value = useMemo(
    () => ({ childDetails, updateChildDetails, clearChildDetails }),
    [childDetails, updateChildDetails, clearChildDetails],
  );

  return (
    <ChildDetailsContext.Provider value={value}>
      {children}
    </ChildDetailsContext.Provider>
  );
};

export const useChildDetails = () => useContext(ChildDetailsContext);

/* ------------------- DISCLAIMER CONTEXT ------------------- */
const DisclaimerContext = createContext(null);

export const DisclaimerProvider = ({ children }) => {
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  const acceptDisclaimer = useCallback(() => setAcceptedDisclaimer(true), []);
  const resetDisclaimer = useCallback(() => setAcceptedDisclaimer(false), []);
  useEffect(() => {
    try {
      const saved = localStorage.getItem("acceptedDisclaimerNew");
      if (saved) {
        const parsed = JSON.parse(saved);
        setAcceptedDisclaimer(parsed === "1" ? true : false);
      }
    } catch (err) {
      console.error("Error reading selectedPlan from localStorage:", err);
      // If invalid data found, clear it
      localStorage.removeItem("acceptedDisclaimerNew");
    }
  }, []);
  const value = useMemo(
    () => ({ acceptedDisclaimer, acceptDisclaimer, resetDisclaimer }),
    [acceptedDisclaimer, acceptDisclaimer, resetDisclaimer],
  );

  return (
    <DisclaimerContext.Provider value={value}>
      {children}
    </DisclaimerContext.Provider>
  );
};

export const useDisclaimer = () => useContext(DisclaimerContext);

/* ------------------- ADDRESS CONTEXT ------------------- */
const AddressContext = createContext(null);

export const AddressProvider = ({ children }) => {
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [selectedMealAddressId, setSelectedMealAddressId] = useState(null);
  const [loading, setLoading] = useState(false); // ✅ global loading state
  const { updateDeliveryChargeByCity } = useCart();
  const { addToast } = useToast();
  const { fetchCart, cart } = useCart();
  const { isLoggedIn } = useAuth();
  const location = useLocation();
  let user = JSON.parse(localStorage.getItem("loggedInUser"));
  // ✅ Fetch all addresses from backend
  const fetchAddresses = useCallback(
    async (type = "order") => {
      setLoading(true);

      try {
        const { data } = await axios.get(
          "/dashboard/customer-shipping-addresses",
        );
        let items = data?.items || [];

        // Filter addresses based on type
        // if (type === "meals") {
        //   items = items.filter((addr) => addr.city?.toLowerCase() === "dubai");
        // }

        setSavedAddresses(items);

        if (items.length === 0) {
          setSelectedAddressId(null);
          setSelectedMealAddressId(null);
          updateDeliveryChargeByCity(null);
          return;
        }

        // Find default address
        const defaultAddress = items.find((addr) => addr.isDefault === "1");

        // Determine selected addresses based on type
        const selectedAddressForOrder =
          type === "order" ? cart?.orderCart?.shippingAddress : defaultAddress;

        const selectedAddressForMeal =
          type === "meals" ? cart?.mealCart?.shippingAddress : defaultAddress;

        // Update order address
        if (selectedAddressForOrder) {
          setSelectedAddressId(selectedAddressForOrder._id);
          updateDeliveryChargeByCity(selectedAddressForOrder.city);
        } else if (defaultAddress) {
          setSelectedAddressId(defaultAddress._id);
          updateDeliveryChargeByCity(defaultAddress.city);
        } else {
          const firstAddress = items[0];
          setSelectedAddressId(firstAddress?._id || null);
          updateDeliveryChargeByCity(firstAddress?.city || null);
        }

        // Update meal address
        if (selectedAddressForMeal) {
          setSelectedMealAddressId(selectedAddressForMeal._id);
          updateDeliveryChargeByCity(selectedAddressForMeal.city);
        } else if (defaultAddress) {
          setSelectedMealAddressId(defaultAddress._id);
          updateDeliveryChargeByCity(defaultAddress.city);
        } else {
          const firstAddress = items[0];
          setSelectedMealAddressId(firstAddress?._id || null);
          updateDeliveryChargeByCity(firstAddress?.city || null);
        }
      } catch (err) {
        console.error("Failed to fetch addresses:", err);
      } finally {
        setLoading(false);
      }
    },
    [cart],
  );

  // ✅ Add new address
  const addAddress = useCallback(async (address, type = "order") => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        "/dashboard/customer-shipping-addresses",
        address,
      );

      // Ensure you return the address object (depends on your API structure)
      const createdAddress = data?.address || data?.data || data;

      // Refresh the list but don't block return
      fetchAddresses(type);

      return true;
    } catch (err) {
      console.error("Failed to add address:", err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Update address
  const editAddress = useCallback(
    async (id, updatedAddress, type = "order") => {
      setLoading(true);
      try {
        await axios.post(
          `/dashboard/customer-shipping-addresses/${id}`,
          updatedAddress,
        );
        await fetchAddresses(type);
        if (selectedAddressId === id) {
          setSelectedAddressId(id);
          setSelectedMealAddressId(id);
        }
        return true;
      } catch (err) {
        console.error("Failed to update address:", err);
      } finally {
        setLoading(false);
      }
    },
    [selectedAddressId, fetchAddresses],
  );

  // ✅ Remove address
  const removeAddress = useCallback(
    async (id, type = "order") => {
      setLoading(true);
      try {
        await axios.delete(`/dashboard/customer-shipping-addresses/${id}`);
        await fetchAddresses(type);

        if (selectedAddressId === id) {
          setSelectedAddressId(null);
          setSelectedMealAddressId(null);
        }

        return true;
      } catch (err) {
        console.error("Failed to delete address:", err);

        // 🔥 Pass the error upward (including 403)
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [selectedAddressId, fetchAddresses],
  );

  const selectDefaultAddress = useCallback(
    async (id, action) => {
      const selectedAddress = savedAddresses.find((addr) => addr._id === id);
      if (!selectedAddress) return;

      try {
        setLoading(true);
        await axios.post(
          "/dashboard/customer-shipping-addresses/set-default-address",
          {
            id: id,
            action,
          },
        );
        addToast(
          `Address  ${
            action != "remove" ? "selected" : "removed"
          } as default address`,
          "success",
        );
        fetchAddresses();
      } catch (error) {
        // addToast("Address selection error", "error");
        console.error("Failed to select address:", error);
      } finally {
        setLoading(false);
      }
    },
    [savedAddresses],
  );
  // ✅ Select an address

  const selectAddress = useCallback(
    async (id, cartId) => {
      const selectedAddress = savedAddresses.find((addr) => addr._id === id);
      if (!selectedAddress) return;

      try {
        setLoading(true);
        await axios.post(
          "/cart/select-customer-shipping-address-for-product-order",
          {
            cartId: cartId,
            addressId: id,
          },
        );

        setSelectedAddressId(id);
        fetchCart();
        return true;
      } catch (error) {
        //addToast("Address selection error", "error");
        console.error("Failed to select address:", error);
      } finally {
        setLoading(false);
      }
    },
    [savedAddresses],
  );
  const selectAddressForMeal = useCallback(
    async (id, cartId) => {
      const selectedAddress = savedAddresses.find((addr) => addr._id === id);
      if (!selectedAddress) return;

      try {
        setLoading(true);
        await axios.post(
          "/cart/select-customer-shipping-address-for-meal-subscription",
          {
            mealSubscriptionCartId: cartId,
            addressId: id,
          },
        );

        setSelectedMealAddressId(id);
        fetchCart();
        return true;
      } catch (error) {
        //addToast("Address selection error", "error");
        console.error("Failed to select address:", error);
        setSelectedMealAddressId(null);
      } finally {
        setLoading(false);
      }
    },
    [savedAddresses],
  );
  // ✅ Clear selections
  const clearSelection = useCallback(() => setSelectedAddressId(null), []);
  const clearAllAddresses = useCallback(() => {
    setSavedAddresses([]);
    setSelectedAddressId(null);
    setSelectedMealAddressId(null);
  }, []);

  // ✅ Memoized context value
  const value = useMemo(
    () => ({
      savedAddresses,
      selectedAddressId,
      loading,
      selectedMealAddressId,
      setSelectedMealAddressId,
      selectAddressForMeal,
      fetchAddresses,
      selectDefaultAddress,
      addAddress,
      editAddress,
      removeAddress,
      selectAddress,
      clearSelection,
      clearAllAddresses,
    }),
    [
      savedAddresses,
      selectedAddressId,
      loading,
      selectedMealAddressId,
      setSelectedMealAddressId,
      selectAddressForMeal,
      fetchAddresses,
      addAddress,
      editAddress,
      selectDefaultAddress,
      removeAddress,
      selectAddress,
      clearSelection,
      clearAllAddresses,
    ],
  );
  return (
    <AddressContext.Provider value={value}>{children}</AddressContext.Provider>
  );
};

export const useAddress = () => useContext(AddressContext);
/* ------------------- ADDRESS CONTEXT ------------------- */

const PromoCodeContext = createContext(null);

export const PromoCodeProvider = ({ children }) => {
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [loading, setLoading] = useState(false);
  const { fetchCart } = useCart();
  const { addToast } = useToast();

  /**
   * ✅ Apply coupon — automatically detects whether it's for product or meal subscription
   */
  const applyCoupon = useCallback(
    async ({ cartId, mealSubscriptionCartId, couponCode }) => {
      const cartIdSaved = localStorage.getItem("cartId");
      try {
        setLoading(true);
        let endpoint = "";
        let payload = {};

        if (mealSubscriptionCartId) {
          endpoint = "/cart/apply-coupon-to-subscription";
          payload = { mealSubscriptionCartId, couponCode };
        } else if (cartIdSaved) {
          endpoint = "/cart/apply-coupon-to-product-order";
          payload = { cartId: cartIdSaved, couponCode };
        } else {
          const errorMsg = "Missing cart information";
          addToast(errorMsg, "error");
          throw new Error(errorMsg);
        }

        const { data, status } = await axios.post(endpoint, payload);

        if (status === 200 || status === 201) {
          setAppliedCoupon(data?.coupon || couponCode);
          addToast("Coupon applied successfully!", "success");
          await fetchCart();
          return { success: true, data };
        } else {
          const errorMsg = data?.message || "Invalid coupon code";
          addToast(errorMsg, "error");
          return { success: false, error: errorMsg };
        }
      } catch (error) {
        console.error("Error applying coupon:", error);
        // addToast("Something went wrong while applying coupon", "error");
        return {
          success: false,
          error: error?.response?.data?.message || error,
        };
      } finally {
        setLoading(false);
      }
    },
    [fetchCart],
  );

  /**
   * ✅ Remove coupon — detects which type of cart to remove from
   */
  const removeCoupon = useCallback(
    async ({ cartId, mealSubscriptionCartId }) => {
      const cartIdSaved = localStorage.getItem("cartId");
      try {
        setLoading(true);
        let endpoint = "";
        let payload = {};

        if (mealSubscriptionCartId) {
          endpoint = "/cart/remove-coupon-from-subscription";
          payload = { mealSubscriptionCartId: mealSubscriptionCartId || "" };
        } else if (cartIdSaved) {
          endpoint = "/cart/remove-coupon-from-product-order";
          payload = { cartId: cartIdSaved };
        } else {
          // console.log(mealSubscriptionCartId, cartIdSaved)
          addToast("Missing cart information", "error");
          return;
        }

        const { data, status } = await axios.post(endpoint, payload);

        if (status === 200 || status === 201) {
          localStorage.removeItem("promoCodeSaved");
          setAppliedCoupon(null);
          addToast("Coupon removed successfully", "success");
          await fetchCart();
          return { success: true, data };
        } else {
          addToast(data?.message || "Failed to remove coupon", "error");
        }
      } catch (error) {
        console.error("Error removing coupon:", error);
        addToast("Failed to remove coupon", "error");
        return {
          success: false,
          error: error?.response?.data?.message || error,
        };
      } finally {
        setLoading(false);
      }
    },
    [fetchCart, addToast],
  );

  /**
   * ✅ Clear local state
   */
  const clearCoupon = useCallback(() => {
    setAppliedCoupon(null);
  }, []);

  const value = useMemo(
    () => ({
      appliedCoupon,
      loading,
      applyCoupon,
      removeCoupon,
      clearCoupon,
    }),
    [appliedCoupon, loading, applyCoupon, removeCoupon, clearCoupon],
  );

  return (
    <PromoCodeContext.Provider value={value}>
      {children}
    </PromoCodeContext.Provider>
  );
};

export const usePromoCode = () => useContext(PromoCodeContext);

/* ------------------- PAYMENT CONTEXT ------------------- */
const PaymentContext = createContext(null);

export const PaymentProvider = ({ children }) => {
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [loading, setLoading] = useState(false);

  // ✅ Fetch all cards from backend
  const fetchCards = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("/dashboard/cards");
      const cards = data?.items || [];
      setSavedCards(cards);

      // Auto-select default card if any, else first card
      if (cards.length > 0) {
        const defaultCard = cards.find((c) => c.isDefault === 1);
        if (defaultCard) setSelectedCardId(defaultCard._id);
        else setSelectedCardId(cards[0]._id);
      } else {
        setSelectedCardId(null);
      }
    } catch (err) {
      console.error("Failed to fetch cards:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ Add a new card
  const addCard = useCallback(
    async (cardData) => {
      setLoading(true);
      try {
        const { data } = await axios.post("/dashboard/cards", cardData);
        setSelectedCardId(data?._id);
        await fetchCards(); // Refresh after add
        return true;
      } catch (err) {
        console.error("Failed to add card:", err);
      } finally {
        setLoading(false);
      }
    },
    [fetchCards],
  );

  // ✅ Update an existing card
  const updateCard = useCallback(
    async (id, updatedData) => {
      setLoading(true);
      try {
        await axios.post(`/dashboard/cards/${id}`, updatedData);
        await fetchCards(); // Refresh after update
        if (selectedCardId === id) setSelectedCardId(id);
        return true;
      } catch (err) {
        console.error("Failed to update card:", err);
      } finally {
        setLoading(false);
      }
    },
    [selectedCardId, fetchCards],
  );

  // ✅ Remove a card
  const removeCard = useCallback(
    async (id) => {
      setLoading(true);
      try {
        await axios.delete(`/dashboard/cards/${id}`);
        await fetchCards(); // Refresh after delete
        if (selectedCardId === id) setSelectedCardId(null);
        return true;
      } catch (err) {
        console.error("Failed to delete card:", err);
      } finally {
        setLoading(false);
      }
    },
    [selectedCardId, fetchCards],
  );

  // ✅ Select a card
  const selectCard = useCallback(
    (id) => {
      if (savedCards.find((card) => card._id === id)) {
        setSelectedCardId(id);
      }
    },
    [savedCards],
  );

  // ✅ Clear all cards
  const clearCards = useCallback(() => {
    setSavedCards([]);
    setSelectedCardId(null);
  }, []);

  // ✅ Memoized context value
  const value = useMemo(
    () => ({
      savedCards,
      selectedCardId,
      setSelectedCardId,
      loading,
      fetchCards,
      addCard,
      updateCard,
      removeCard,
      selectCard,
      clearCards,
    }),
    [
      savedCards,
      selectedCardId,
      setSelectedCardId,
      loading,
      fetchCards,
      addCard,
      updateCard,
      removeCard,
      selectCard,
      clearCards,
    ],
  );

  return (
    <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>
  );
};

export const usePayment = () => useContext(PaymentContext);
/* ------------------- RESET STEP 1 CONTEXT ------------------- */

/* ------------------- KID CONTEXT ------------------- */
const KidContext = createContext(null);

export const KidProvider = ({ children }) => {
  const [savedKids, setSavedKids] = useState([]);
  const [selectedKidId, setSelectedKidId] = useState(null);
  const [loading, setLoading] = useState(false);

  const selectedChild = JSON.parse(localStorage.getItem("selectedChildNew"));
  const childId = selectedChild?._id;

const fetchKids = useCallback(async () => {
  setLoading(true);
  try {
    const { data } = await axios.get("/dashboard/kids");
    const cards = data?.items || [];
    setSavedKids(cards);

    // read fresh, not from an outer stale closure
    const stored = localStorage.getItem("selectedChildNew");
    const parsed = stored && stored !== "undefined" ? JSON.parse(stored) : null;
    const currentChildId = parsed?._id;

    if (currentChildId && cards.some((c) => c._id === currentChildId)) {
      setSelectedKidId(currentChildId);
    }
  } catch (err) {
    console.error("Failed to fetch cards:", err);
  } finally {
    setLoading(false);
  }
}, []); // now safe to keep empty deps — nothing stale is captured

  // ✅ Add a new card
  const addKid = useCallback(
    async (kidData) => {
      setLoading(true);
      try {
        const { data } = await axios.post("/dashboard/kids", kidData);
        await fetchKids(); // Refresh after add
        return true;
      } catch (err) {
        console.error("Failed to add card:", err);
      } finally {
        setLoading(false);
      }
    },
    [fetchKids],
  );

  // ✅ Update an existing card
  const updateKid = useCallback(
    async (id, updatedData) => {
      setLoading(true);
      try {
        await axios.post(`/dashboard/kids/${id}`, updatedData);
        await fetchKids(); // Refresh after update
        if (selectedKidId === id) selectedKidId(id);
        return true;
      } catch (err) {
        console.error("Failed to update card:", err);
      } finally {
        setLoading(false);
      }
    },
    [selectedKidId, fetchKids],
  );

  // ✅ Remove a card
  // const removeCard = useCallback(
  //   async (id) => {
  //     setLoading(true);
  //     try {
  //       await axios.delete(`/dashboard/cards/${id}`);
  //       await fetchCards(); // Refresh after delete
  //       if (selectedCardId === id) setSelectedCardId(null);
  //       return true;
  //     } catch (err) {
  //       console.error("Failed to delete card:", err);
  //     } finally {
  //       setLoading(false);
  //     }
  //   },
  //   [selectedCardId, fetchCards]
  // );

  // ✅ Select a card
  const selectKid = useCallback(
    (id) => {
      if (savedKids.find((card) => card._id === id)) {
        setSelectedKidId(id);
      }
    },
    [savedKids],
  );

  // ✅ Memoized context value
  const value = useMemo(
    () => ({
      savedKids,
      selectedKidId,
      setSelectedKidId,
      loading,
      fetchKids,
      addKid,
      updateKid,
      selectKid,
    }),
    [
      savedKids,
      selectedKidId,
      setSelectedKidId,
      loading,
      fetchKids,
      addKid,
      updateKid,
      selectKid,
    ],
  );

  return <KidContext.Provider value={value}>{children}</KidContext.Provider>;
};

export const useKid = () => useContext(KidContext);
/* ------------------- RESET STEP 1 CONTEXT ------------------- */

const ResetStep1Context = createContext(null);

const ResetStep1Provider = ({ children }) => {
  const { setSelectedPlan } = usePlan();
  const { setSelectedMealType } = useMealType();
  const { clearMeals } = useMealsSelection();

  const resetStep1 = () => {
    setSelectedPlan(null);
    setSelectedMealType(null);
    clearMeals();
  };

  const value = useMemo(() => ({ resetStep1 }), []);

  return (
    <ResetStep1Context.Provider value={value}>
      {children}
    </ResetStep1Context.Provider>
  );
};

export const useResetStep1 = () => useContext(ResetStep1Context);

/*-------------------------------- DATE CONTEXT -----------------------------------*/

const DateContext = createContext(null);

export const DateProvider = ({ children }) => {
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("selectedDateNew");
      if (saved) {
        const parsed = JSON.parse(saved);
        setSelectedDate(parsed);
      }
    } catch (err) {
      console.error("Error reading selectedDate from localStorage:", err);
      // Clear invalid data
      localStorage.removeItem("selectedDateNew");
    }
  }, []);

  const value = useMemo(
    () => ({ selectedDate, setSelectedDate }),
    [selectedDate],
  );

  return <DateContext.Provider value={value}>{children}</DateContext.Provider>;
};

export const useDate = () => useContext(DateContext);

/* ------------------- COMBINED SUBSCRIPTION PROVIDER ------------------- */
export const SubscriptionProvider = ({ children }) => (
  <StepProvider>
    <PlanProvider>
      <MealTypeProvider>
        <MealsSelectionProvider>
          <KidProvider>
            <DateProvider>
              <ChildDetailsProvider>
                <DisclaimerProvider>
                  <AddressProvider>
                    <PromoCodeProvider>
                      <PaymentProvider>
                        <ResetStep1Provider>{children}</ResetStep1Provider>
                      </PaymentProvider>
                    </PromoCodeProvider>
                  </AddressProvider>
                </DisclaimerProvider>
              </ChildDetailsProvider>
            </DateProvider>
          </KidProvider>
        </MealsSelectionProvider>
      </MealTypeProvider>
    </PlanProvider>
  </StepProvider>
);
